import { SHELL_FONT_FAMILY } from "@slot/presentation";
import type { ViewportStore } from "@slot/presentation";
import { landscapeMultipliers } from "../chrome/chromeContentArea";

/**
 * At the right end of the header strip (100 tall, ×m in mobile landscape), clear of its clock and title on the left. Until
 * 2026-10-01 the badge was the Cocos boot scene's `DevBadge`: a plain label in a 100² box at left 30 / top 30, under the
 * clock, which nobody read as a button once it opened the dev menu (decision 37).
 */
const BADGE = { right: 24, top: 8, height: 84, paddingX: 22, gap: 14, fontSize: 30, radius: 16, border: 2, glyph: { width: 30, height: 22, bar: 4 } } as const;

const du = (units: number): string => `calc(var(--du) * ${units})`;

/**
 * The dev badge as a DOM element in the overlay root, which outlives every scene change. Sizes are design units through
 * `--du`. It is a plain "DEV" label until the dev menu takes its tap (`setOnTap`); from then on it looks like the
 * button it is: a dark chip with a menu glyph.
 */
export class DevBadgeView {
  private readonly root: HTMLDivElement;
  private readonly glyph: HTMLSpanElement;
  private readonly label: HTMLSpanElement;
  private readonly unsubscribe: () => void;

  public constructor(input: { overlayRoot: HTMLElement; viewport: ViewportStore }) {
    this.root = document.createElement("div");
    this.root.dataset.slot = "dev-badge";
    const style = this.root.style;
    style.position = "absolute";
    style.right = du(BADGE.right);
    style.top = du(BADGE.top);
    style.height = du(BADGE.height);
    style.boxSizing = "border-box";
    style.display = "flex";
    style.alignItems = "center";
    style.gap = du(BADGE.gap);
    style.padding = `0 ${du(BADGE.paddingX)}`;
    style.borderRadius = du(BADGE.radius);
    style.border = `${du(BADGE.border)} solid transparent`;
    style.fontFamily = SHELL_FONT_FAMILY;
    style.fontWeight = "700";
    style.fontSize = du(BADGE.fontSize);
    style.letterSpacing = "0.06em";
    style.color = "#fff";
    style.textShadow = "0 0 4px rgba(0, 0, 0, 0.8)";
    style.whiteSpace = "nowrap";
    style.userSelect = "none";
    style.touchAction = "manipulation";
    this.glyph = document.createElement("span");
    this.glyph.dataset.part = "glyph";
    const bar = `linear-gradient(#fff, #fff)`;
    this.glyph.style.cssText = `width: ${du(BADGE.glyph.width)}; height: ${du(BADGE.glyph.height)}; background: ${bar} top / 100% ${du(BADGE.glyph.bar)} no-repeat, ${bar} center / 100% ${du(BADGE.glyph.bar)} no-repeat, ${bar} bottom / 100% ${du(BADGE.glyph.bar)} no-repeat;`;
    this.label = document.createElement("span");
    this.root.append(this.glyph, this.label);
    this.applyLook(false);
    this.root.hidden = true;
    input.overlayRoot.appendChild(this.root);
    this.unsubscribe = input.viewport.subscribe((v) => {
      // The header strip scales by the landscape multiplier and the badge scales with it. The overlay root is a sibling of
      // the chrome root: no inherited `--slot-landscape-m`, the same value computed here.
      const m = v.shape === "mobile_landscape" ? landscapeMultipliers(v).m : 1;
      this.root.style.setProperty("--du", `${v.scale * m}px`);
    });
  }

  public setVisible(visible: boolean): void {
    this.root.hidden = !visible;
  }

  public setText(text: string): void {
    this.label.textContent = text;
  }

  /** A tap on the badge opens and closes the dev menu; null leaves it an inert label, as it is until the menu exists. */
  public setOnTap(onTap: (() => void) | null): void {
    this.root.onclick = onTap ? () => onTap() : null;
    this.applyLook(onTap !== null);
  }

  public dispose(): void {
    this.unsubscribe();
    this.root.remove();
  }

  private applyLook(button: boolean): void {
    const style = this.root.style;
    style.pointerEvents = button ? "auto" : "none";
    style.cursor = button ? "pointer" : "";
    style.background = button ? "rgba(16, 18, 24, 0.72)" : "transparent";
    style.borderColor = button ? "rgba(255, 255, 255, 0.5)" : "transparent";
    this.glyph.hidden = !button;
    if (button) {
      this.root.setAttribute("role", "button");
      this.root.title = "Dev menu (the ` key)";
    } else {
      this.root.removeAttribute("role");
      this.root.removeAttribute("title");
    }
  }
}
